// MorgenVerwachtTile — Dashboard cycle #50 (Bram feedback 2026-05-22).
//
// PV-forecast voor morgen met visuele curve. Vervangt de oude tekstuele tile.
// Bram: "PV verwacht heeft veel ruimte om de huidige tekst visueel
// aantrekkelijker weer te geven."
//
// Layout (mobile, full-width via .kpi-tile-double wrapper):
//   - Label: MORGEN VERWACHT
//   - Hoofdwaarde: totaal kWh (zon-icoon + getal)
//   - Mini sparkline van uurlijkse PV-forecast (00..24)
//   - Subline 1: vandaag verwacht + gerealiseerd (progress)
//   - Subline 2: pv_aware_fill badge (vol-laden / wacht op morgen)

import './kpi.css';
import type { CSSProperties, ReactNode } from 'react';
import { fmtKwh } from '../../lib/format';
import type { PvForecastResponse } from '../../api/types';

interface Props {
  /** PV-forecast endpoint response (volgende ~48u) */
  pvForecast: PvForecastResponse | null;
  /** Vandaag al gerealiseerd (kWh) - uit snapshot pv counters */
  todayRealisedKwh: number | null;
  /** Vandaag projected total (gerealiseerd + forecast-rest) */
  todayProjectedKwh: number | null;
  /** PV-aware fill badge (vol-laden mode etc.) — render-resultaat van renderPvFillBadge */
  pvFillBadge: ReactNode;
}

export function MorgenVerwachtTile({
  pvForecast,
  todayRealisedKwh,
  todayProjectedKwh,
  pvFillBadge,
}: Props) {
  // Filter morgen-uren uit forecast. Lokale datum-toString = locale TZ.
  const tomorrowHours = pvForecast?.hours
    ? pvForecast.hours.filter((h) => {
        const d = new Date(h.hour_start);
        const tom = new Date();
        tom.setDate(tom.getDate() + 1);
        return d.toDateString() === tom.toDateString();
      })
    : [];

  const tomorrowKwh = tomorrowHours.reduce((sum, h) => sum + (h.pv_kwh ?? 0), 0);
  const peakKwh = tomorrowHours.reduce((m, h) => Math.max(m, h.pv_kwh ?? 0), 0);

  // Compose 24 sparkline values uit forecast. Als er geen morgen-data is, vul met 0.
  // We mappen hour-of-day (0..23) zodat de bar-positie matcht met dagcurve.
  const sparkValues: number[] = Array(24).fill(0);
  for (const h of tomorrowHours) {
    const d = new Date(h.hour_start);
    const hod = d.getHours();
    if (hod >= 0 && hod < 24) {
      sparkValues[hod] = h.pv_kwh ?? 0;
    }
  }

  // Vinger-friendly: vandaag-subline samenvatten als "vandaag X kWh (Y gerealiseerd)"
  // Voor langere uitleg: tooltip.
  const todaySub =
    todayProjectedKwh != null && todayRealisedKwh != null
      ? `vandaag ${fmtKwh(todayProjectedKwh)} (${fmtKwh(todayRealisedKwh)} gerealiseerd)`
      : todayProjectedKwh != null
        ? `vandaag verwacht ${fmtKwh(todayProjectedKwh)}`
        : null;

  return (
    <div
      className="kpi-tile morgen-verwacht-tile"
      style={{ ['--accent' as string]: '#38bdf8' } as CSSProperties}
      title={[
        `Morgen totaal verwacht: ${fmtKwh(tomorrowKwh)}`,
        `Piek-uur: ${fmtKwh(peakKwh)} kWh`,
        peakKwh > 0 ? '' : 'Forecast nog niet gepubliceerd',
      ].join('\n')}
    >
      <div className="morgen-verwacht-head">
        <div className="morgen-verwacht-text">
          <div className="kpi-label">Morgen verwacht</div>
          <div className="kpi-value">
            <span className="morgen-verwacht-sun" aria-hidden>☀</span>
            {fmtKwh(tomorrowKwh)}
            <span className="unit"> PV</span>
          </div>
        </div>
        {peakKwh > 0 && (
          <Sparkline values={sparkValues} peak={peakKwh} />
        )}
      </div>
      {todaySub && (
        <div className="kpi-sub morgen-verwacht-today-sub">{todaySub}</div>
      )}
      {pvFillBadge && (
        <div className="morgen-verwacht-badge-row">{pvFillBadge}</div>
      )}
    </div>
  );
}

/**
 * Sparkline — eenvoudige bar-rij van 24 uur PV-forecast. Geen externe lib —
 * pure SVG. Sun-icon aan begin/eind irrelevant: bars zelf vormen de curve.
 *
 * Hoogte: 36px. Bar-breedte: container-w / 24 - 1px gap. Kleur: lichtblauw
 * (zelfde als tile accent) met opacity ~0.85.
 */
function Sparkline({ values, peak }: { values: number[]; peak: number }) {
  if (peak <= 0) return null;
  const W = 110;
  const H = 36;
  const N = values.length;
  const barW = W / N;
  return (
    <svg
      className="morgen-verwacht-spark"
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      {/* baseline */}
      <line x1={0} y1={H - 0.5} x2={W} y2={H - 0.5} stroke="#e5e7eb" strokeWidth={1} />
      {values.map((v, i) => {
        const h = peak > 0 ? (v / peak) * (H - 2) : 0;
        const x = i * barW + 0.5;
        const y = H - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={Math.max(0, barW - 1)}
            height={h}
            fill="#38bdf8"
            opacity={v > 0 ? 0.85 : 0}
          />
        );
      })}
    </svg>
  );
}
